import React from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, BookOpen, History, MessageCircle, LogOut, LifeBuoy, Wallet, CreditCard, KeyRound, Copy, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { DASHBOARD, AUTH } from "@/constants/testIds";
import BrandMark from "@/components/BrandMark";
import DisclaimerBanner from "@/components/DisclaimerBanner";
import CallFromDetentionCard from "@/components/CallFromDetentionCard";
import WelcomeGuide from "@/components/WelcomeGuide";
import FamilyHelpCard from "@/components/FamilyHelpCard";
import PreventiveProtectionPlan from "@/components/PreventiveProtectionPlan";
import ResumeCallCard from "@/components/ResumeCallCard";
import PrivacyControlsCard from "@/components/PrivacyControlsCard";
import RedFlagsCard from "@/components/RedFlagsCard";
import LegalValidationBadge from "@/components/LegalValidationBadge";
import ValuableResourceCard from "@/components/ValuableResourceCard";
import SendI589EmailCard from "@/components/SendI589EmailCard";

const PLAN_LABELS = {
  family: "Familiar",
  personal: "Personal",
};
import { useState } from "react";
import { toast } from "sonner";

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [copied, setCopied] = useState(false);

  const copyPin = () => {
    if (!user?.pin) return;
    navigator.clipboard.writeText(user.pin);
    setCopied(true);
    toast.success("PIN copiado");
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <div className="min-h-screen bg-zinc-50 pb-28">
      <div className="max-w-md mx-auto w-full px-5 pt-8">
        <div className="flex items-start justify-between mb-8">
          <div>
            <BrandMark size="sm" />
            <h1 className="text-2xl sm:text-3xl font-black tracking-tight text-zinc-900 mt-4">
              Hola{user?.username ? `, ${user.username}` : ""}
            </h1>
            <p className="text-sm text-zinc-600 mt-1">
              Estás a un toque de tus derechos.
            </p>
          </div>
          <button
            data-testid={AUTH.logout}
            onClick={() => {
              logout();
              navigate("/");
            }}
            className="p-2 rounded-lg hover:bg-zinc-100 text-zinc-500"
            aria-label="Cerrar sesión"
          >
            <LogOut className="w-5 h-5" />
          </button>
        </div>

        {/* Welcome guide explaining the 4-step service flow */}
        <WelcomeGuide user={user} />

        {/* PIN card - prominently shown only when user has one (after payment) */}
        {user?.pin && (
          <div
            data-testid="dashboard-pin-card"
            className="rounded-2xl p-5 mb-4 bg-gradient-to-br from-[#075E54] via-[#128C7E] to-[#25D366] text-white relative overflow-hidden shadow-xl"
          >
            <div className="absolute inset-0 opacity-10 pointer-events-none">
              <div className="absolute -top-8 -right-8 w-40 h-40 bg-white rounded-full blur-3xl" />
            </div>
            <div className="relative">
              <div className="flex items-center gap-2 mb-3">
                <KeyRound className="w-5 h-5" />
                <p className="text-[10px] uppercase tracking-widest font-black opacity-90">
                  AQUÍ TIENES TU PIN DE USUARIO
                </p>
              </div>
              <p className="text-sm leading-snug opacity-95 mb-4">
                Tenlo en mente o a la mano. Cuando necesites hacer llamadas de emergencia o desde un centro de detención, nuestro sistema te pedirá este número para enlazar la llamada.
              </p>
              <div className="flex items-center justify-between gap-3 bg-white/10 backdrop-blur border border-white/30 rounded-2xl p-4">
                <p
                  data-testid="dashboard-pin-value"
                  className="text-4xl sm:text-5xl font-black tracking-[0.3em] font-mono select-all"
                >
                  {user.pin}
                </p>
                <button
                  data-testid="dashboard-pin-copy"
                  onClick={copyPin}
                  className="flex-shrink-0 w-12 h-12 rounded-xl bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
                  aria-label="Copiar PIN"
                >
                  {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Detention-center call instructions (Twilio number + PAN List) */}
        {user?.pin && <CallFromDetentionCard />}

        {/* Resume last active conversation — shown only if there is one with messages */}
        {user?.pin && <ResumeCallCard />}

        {/* Preventive Protection Plan — prepare I-589 BEFORE detention */}
        {user?.pin && <PreventiveProtectionPlan />}

        {/* Family/friend help: download forms, glossary, instructions */}
        {user?.pin && <FamilyHelpCard />}

        {/* External authoritative pro-se asylum guide (Stanford Law + Centro Legal + CLSEPA) */}
        {user?.pin && (
          <div className="mb-5">
            <ValuableResourceCard />
          </div>
        )}

        {/* Red flags — when to escalate to a real attorney */}
        {user?.pin && (
          <div className="mb-5">
            <RedFlagsCard />
          </div>
        )}

        {/* Legal framework validation badge */}
        {user?.pin && (
          <div className="mb-5">
            <LegalValidationBadge />
          </div>
        )}

        {/* Privacy controls: explicit data-retention policy + wipe button */}
        {user?.pin && <PrivacyControlsCard />}

        {/* Plan & balance */}
        <div
          data-testid="dashboard-plan-card"
          className={`rounded-2xl p-5 mb-5 ${
            user?.plan === "free" || user?.plan === undefined
              ? "bg-zinc-900 text-white"
              : "bg-gradient-to-br from-emerald-600 to-emerald-800 text-white"
          }`}
        >
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="flex items-center gap-2">
              <Wallet className="w-5 h-5" />
              <p className="text-[10px] uppercase tracking-widest font-black opacity-80">
                Mi plan
              </p>
            </div>
            <span className="text-xs font-black uppercase bg-white/20 px-2 py-0.5 rounded-full">
              {PLAN_LABELS[user?.plan] || "Gratis"}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div>
              <p className="text-3xl font-black tracking-tighter">{user?.calls_emergency_remaining ?? 0}</p>
              <p className="text-[11px] opacity-80 leading-tight">Llamadas de emergencia (frente a ICE)</p>
            </div>
            <div>
              <p className="text-3xl font-black tracking-tighter">{user?.calls_detention_remaining ?? 0}</p>
              <p className="text-[11px] opacity-80 leading-tight">Llamadas en centro de detención</p>
            </div>
          </div>
          <button
            data-testid="dashboard-comprar-plan"
            onClick={() => navigate("/precios")}
            className="w-full h-11 bg-white/10 hover:bg-white/20 backdrop-blur border border-white/30 rounded-xl text-sm font-black uppercase tracking-wide flex items-center justify-center gap-2 transition-colors"
          >
            <CreditCard className="w-4 h-4" />
            {user?.plan === "free" || user?.plan === undefined ? "Comprar plan" : "Comprar más llamadas"}
          </button>
        </div>

        {/* Massive emergency button */}
        <button
          data-testid={DASHBOARD.emergencyButton}
          onClick={() => navigate("/emergency")}
          className="w-full h-28 mb-6 text-2xl font-black bg-red-600 hover:bg-red-700 text-white rounded-2xl shadow-[0_12px_40px_rgba(220,38,38,0.4)] active:scale-95 transition-all uppercase flex items-center justify-center gap-3 emergency-pulse"
        >
          <AlertTriangle className="w-9 h-9" strokeWidth={2.75} />
          <span className="text-left leading-tight">
            Estoy siendo
            <br />
            detenido
          </span>
        </button>

        <p className="text-sm text-zinc-600 text-center mb-8 px-2">
          Presiónalo si ICE está cerca, en tu puerta o ya te detuvieron.
        </p>

        {/* Email I-589 to user for printing — Zero-Data, 3 sends max per PIN */}
        <SendI589EmailCard />

        <div className="grid grid-cols-1 gap-3">
          <button
            data-testid={DASHBOARD.phase2Button}
            onClick={() => navigate("/manuales")}
            className="w-full h-20 bg-white border-2 border-zinc-200 hover:border-blue-800 hover:bg-blue-50 text-zinc-900 rounded-2xl transition-colors p-4 flex items-center gap-4"
          >
            <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
              <BookOpen className="w-6 h-6 text-blue-800" strokeWidth={2.5} />
            </div>
            <div className="text-left">
              <div className="font-bold text-lg leading-tight">Manuales de Autoayuda</div>
              <div className="text-xs text-zinc-600">I-589, EOIR-28, EOIR-26, Fianza · PBNDS §6.3</div>
            </div>
          </button>

          <button
            data-testid={DASHBOARD.generalChat}
            onClick={() => navigate("/scenario/general")}
            className="w-full h-20 bg-white border-2 border-zinc-200 hover:border-blue-800 hover:bg-blue-50 text-zinc-900 rounded-2xl transition-colors p-4 flex items-center gap-4"
          >
            <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
              <MessageCircle className="w-6 h-6 text-emerald-700" strokeWidth={2.5} />
            </div>
            <div className="text-left">
              <div className="font-bold text-lg leading-tight">Pregúntale al asistente</div>
              <div className="text-xs text-zinc-600">Información general en español</div>
            </div>
          </button>

          <button
            data-testid={DASHBOARD.historyButton}
            onClick={() => navigate("/history")}
            className="w-full h-20 bg-white border-2 border-zinc-200 hover:border-blue-800 hover:bg-blue-50 text-zinc-900 rounded-2xl transition-colors p-4 flex items-center gap-4"
          >
            <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
              <History className="w-6 h-6 text-amber-700" strokeWidth={2.5} />
            </div>
            <div className="text-left">
              <div className="font-bold text-lg leading-tight">Mi historial</div>
              <div className="text-xs text-zinc-600">Conversaciones anteriores</div>
            </div>
          </button>

          <button
            data-testid="dashboard-recursos-button"
            onClick={() => navigate("/recursos")}
            className="w-full h-20 bg-white border-2 border-zinc-200 hover:border-blue-800 hover:bg-blue-50 text-zinc-900 rounded-2xl transition-colors p-4 flex items-center gap-4"
          >
            <div className="w-12 h-12 rounded-xl bg-rose-100 flex items-center justify-center flex-shrink-0">
              <LifeBuoy className="w-6 h-6 text-rose-700" strokeWidth={2.5} />
            </div>
            <div className="text-left">
              <div className="font-bold text-lg leading-tight">Recursos · Consulados y Ayuda</div>
              <div className="text-xs text-zinc-600">Consulados latinos · Abogados pro bono · Noticias</div>
            </div>
          </button>
        </div>
      </div>
      <DisclaimerBanner />
    </div>
  );
}
